import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import { DashboardIntegrated } from "./components/DashboardIntegrated"; // New Dashboard (work in progress)
import { HelloTest } from "./components/HelloTest"; // Test page
import VoteAnalysis from "./components/VoteAnalysis";
import AdminPage from "./components/AdminPage";
import OnboardingFlow from "./components/OnboardingFlow";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
import PrivacyPolicy from './components/PrivacyPolicy';
import privacyData from './data/privacy.json';
import termsData from './data/terms.json';
import Challenges from './components/ChallengesNew';  // Import Challenges component
import CDDIDemo from './components/CDDIDemo';  // Import CDDIDemo component
import CDDIBatchUpload from './components/CDDIBatchUpload';
import CDDIResults from './components/CDDIResults';
import { onAuthStateChanged, initializeKeycloak } from "./firebase";
import { initializeAuthListener, fetchUserWorkgroups, isOnboardingComplete, canAccessIntegrations, canAccessDevs, canAccessCddi } from './components/firestoreFunctions';  // Import from firebaseFunctions
import { DialogProvider } from './providers/DialogProvider';
import "./styles/themes/color-schemes.css"; // Import color schemes
import IntegrationManager from "./components/IntegrationManagerNew";
import TopNavSimple from "./components/TopNavSimple";

// Protected Route component
const ProtectedRoute = ({ children, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    const checkUserStatus = async () => {
      console.log('ProtectedRoute: Checking user status');
      if (!user) {
        console.log('ProtectedRoute: No user, redirecting to login');
        // Save the attempted URL to redirect back after login
        const returnUrl = location.pathname + location.search;
        navigate(`/?returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }

      try {
        // Check if user has completed onboarding
        console.log('ProtectedRoute: Checking onboarding status for user:', user.uid);
        const onboardingComplete = await isOnboardingComplete(user.uid);
        console.log('ProtectedRoute: Onboarding complete status:', onboardingComplete);
        if (!onboardingComplete) {
          console.log('ProtectedRoute: User needs onboarding, redirecting to OnboardingFlow');
          setNeedsOnboarding(true);
        } else {
          console.log('ProtectedRoute: User onboarding complete, proceeding to protected route');
        }
      } catch (error) {
        console.error("ProtectedRoute: Error checking onboarding status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkUserStatus();
  }, [user, navigate, location]);

  if (!user || isLoading) {
    console.log('ProtectedRoute: Loading or no user, showing loading spinner');
    return (
      <div className="min-h-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (needsOnboarding) {
    console.log('ProtectedRoute: Rendering OnboardingFlow');
    return <OnboardingFlow user={user} />;
  }

  console.log('ProtectedRoute: Rendering protected children');
  return children;
};

const Unauthorized401 = () => (
  <div className="min-h-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="max-w-md w-full space-y-6 p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg text-center">
      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">401 · Unauthorized</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Access to this page is not enabled for your account. If you believe this is an error, contact support@interpretos.ai.
      </p>
    </div>
  </div>
);

const IntegrationProtectedRoute = ({ user, children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const validateAccess = async () => {
      try {
        const allowed = await canAccessIntegrations(user?.uid);
        if (!cancelled) {
          setHasAccess(Boolean(allowed));
        }
      } catch (error) {
        console.error('IntegrationProtectedRoute: unable to verify access', error);
        if (!cancelled) {
          setHasAccess(false);
        }
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    validateAccess();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (isChecking) {
    return (
      <div className="min-h-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return <Unauthorized401 />;
  }

  return children;
};

const DevProtectedRoute = ({ user, children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const userId = user?.uid;

    const validateAccess = async () => {
      if (!userId) {
        if (!cancelled) {
          setHasAccess(false);
          setIsChecking(false);
        }
        return;
      }

      try {
        const allowed = await canAccessDevs(userId);
        if (!cancelled) {
          setHasAccess(Boolean(allowed));
        }
      } catch (error) {
        console.error('DevProtectedRoute: unable to verify access', error);
        if (!cancelled) {
          setHasAccess(false);
        }
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    validateAccess();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  if (isChecking) {
    return (
      <div className="min-h-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/unauthorised401" replace />;
  }

  return children;
};

const CddiProtectedRoute = ({ user, children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const userId = user?.uid;

    const validateAccess = async () => {
      if (!userId) {
        if (!cancelled) {
          setHasAccess(false);
          setIsChecking(false);
        }
        return;
      }

      try {
        const allowed = await canAccessCddi(userId);
        if (!cancelled) {
          setHasAccess(Boolean(allowed));
        }
      } catch (error) {
        console.error('CddiProtectedRoute: unable to verify access', error);
        if (!cancelled) {
          setHasAccess(false);
        }
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    validateAccess();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  if (isChecking) {
    return (
      <div className="min-h-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/unauthorised401" replace />;
  }

  return children;
};

  const App = () => {
    const [user, setUser] = useState(null);
    const [theme, setTheme] = useState('light');
    const [isLoading, setIsLoading] = useState(true);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, []);

  // Listen for user authentication state changes
  useEffect(() => {
    console.log("Starting auth state change");
    
    // Initialize Keycloak and setup auth state listener
    const setupAuth = async () => {
      try {
        await initializeKeycloak();
        initializeAuthListener();
        
        const unsubscribe = onAuthStateChanged((currentUser) => {
          console.log("Auth state changed:", currentUser);
      if (currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });

        return unsubscribe;
      } catch (error) {
        console.error("Failed to setup authentication:", error);
        setIsLoading(false);
      }
    };

    let unsubscribe;
    setupAuth().then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // handleLogin function removed - login now handled by Keycloak directly

  const handleLogout = async () => {
    try {
      const { logout } = await import('./firebase');
      await logout();
    } catch (error) {
      console.error("Error during sign-out:", error);
    }
  };

  // Add mobile detection 
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Global layout guard: keep CSS var synced; shell stays at top and spacer handles offset
  useEffect(() => {
    const enforce = () => {
      try {
        const spacer = document.getElementById('topnav-spacer');
        const measured = spacer?.style.height || getComputedStyle(document.documentElement).getPropertyValue('--topnav-height').trim() || '56px';
        document.documentElement.style.setProperty('--topnav-height', measured);
        if (document.body) document.body.style.setProperty('--topnav-height', measured);
      } catch {}
    };
    enforce();
    window.addEventListener('resize', enforce);
    const mo = new MutationObserver(enforce);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    return () => {
      window.removeEventListener('resize', enforce);
      mo.disconnect();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <DialogProvider theme={theme}>
      <Router>
        <div className={`app-container ${isMobile ? 'mobile-view' : ''}`}>
          {user && (
            <>
              <TopNavSimple user={user} onLogout={handleLogout} />
              <div
                id="topnav-spacer"
                aria-hidden="true"
                style={{ height: 'var(--topnav-height, 56px)' }}
              />
            </>
          )}
          <style>
            {`
              /* App shell pinned below TopNav; screens scroll inside */
              .main-shell {
                position: fixed;
                top: var(--topnav-height, 56px);
                left: 0; right: 0; bottom: 0;
                display: flex;
                flex-direction: column;
                overflow-y: auto;
                overflow-x: hidden;
                background-color: #e8effa; /* ensure scrolled area matches page background */
              }
              /* Ensure route roots can stretch to full shell height */
              .main-shell > * { min-height: 100%; }

              @media (max-width: 767px) {
                html, body {
                  overflow-x: hidden !important;
                  /* Allow natural page scrolling on mobile */
                  overflow-y: auto !important;
                  width: 100% !important;
                  max-width: 100% !important;
                  position: relative !important;
                  height: auto !important;
                  min-height: 100% !important;
                }
                .mobile-view {
                  max-width: 100vw;
                  width: 100vw;
                  overflow-x: hidden;
                  overflow-y: auto !important;
                  height: auto !important;
                  min-height: 100vh !important;
                }
                .overflow-x-auto {
                  overflow-x: auto !important;
                  max-width: 100vw !important;
                  width: 100% !important;
                }
                table {
                  width: auto !important;
                  table-layout: auto !important;
                }
                .prompt-container, .prompt-form {
                  width: 100% !important;
                  max-width: 100vw !important;
                  padding: 0 !important;
                  margin: 0 !important;
                }
                .rounded-full {
                  border-radius: 999px !important;
                  max-width: 85vw !important;
                  margin: 0 auto !important;
                  width: 100% !important;
                }
                /* Hide large internal pane on narrow screens */
                .internal-content {
                  display: none !important;
                  width: 0 !important;
                  min-width: 0 !important;
                  max-width: 0 !important;
                  height: 0 !important;
                }
                /* Remove any rules that hid vertical scrollbars on #root */
                #root {
                  width: 100vw !important;
                  max-width: 100vw !important;
                  overflow-x: hidden !important;
                  overflow-y: auto !important;
                  height: auto !important;
                  min-height: 100vh !important;
                }
                textarea, input {
                  max-width: 85vw !important;
                }
              }
            `}
          </style>
          <div className="main-shell">
          <Routes>
            <Route
              path="/"
              element={
                isLoading ? (
                  <div className="min-h-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                ) : user ? (
                  <ProtectedRoute user={user}>
                    <DashboardIntegrated user={user} onLogout={handleLogout} />
                  </ProtectedRoute>
                ) : (
                  <Login />
                )
              }
            />
            {/* Redirect /signup to home - registration handled by Keycloak */}
            <Route path="/signup" element={<Navigate to="/" replace />} />
            {/* Old dashboard kept for reference */}
            { process.env.NODE_ENV=="development" && <Route
              path="/dashboard-old"
              element={
                <ProtectedRoute user={user}>
                  <DashboardWithAdmin user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />}
            <Route path="/terms" element={<PrivacyPolicy policyData={termsData} />} />
            <Route path="/privacy" element={<PrivacyPolicy policyData={privacyData} />} />
            <Route path="/unauthorised401" element={<Unauthorized401 />} />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute user={user}>
                  <AdminPage user={user}/>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute user={user}>
                  <IntegrationProtectedRoute user={user}>
                    <IntegrationManager />
                  </IntegrationProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cddi-demo"
              element={
                <ProtectedRoute user={user}>
                  <IntegrationProtectedRoute user={user}>
                    <CddiProtectedRoute user={user}>
                      <DevProtectedRoute user={user}>
                        <CDDIDemo />
                      </DevProtectedRoute>
                    </CddiProtectedRoute>
                  </IntegrationProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/challenges"
              element={
                <ProtectedRoute user={user}>
                  <IntegrationProtectedRoute user={user}>
                    <CddiProtectedRoute user={user}>
                      <Challenges />
                    </CddiProtectedRoute>
                  </IntegrationProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cddi-batch"
              element={
                <ProtectedRoute user={user}>
                  <IntegrationProtectedRoute user={user}>
                    <CddiProtectedRoute user={user}>
                      <CDDIBatchUpload />
                    </CddiProtectedRoute>
                  </IntegrationProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cddi-results"
              element={
                <ProtectedRoute user={user}>
                  <IntegrationProtectedRoute user={user}>
                    <CddiProtectedRoute user={user}>
                      <CDDIResults />
                    </CddiProtectedRoute>
                  </IntegrationProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/success" 
              element={
                <ProtectedRoute user={user}>
                  <PaymentSuccess user={user}/>
                </ProtectedRoute>
              } 
            />
            <Route path="/cancel" element={<PaymentCancel />} />
            {/* Dev-only test route guarded by specialist role */}
            <Route
              path="/hello-test"
              element={
                <ProtectedRoute user={user}>
                  <DevProtectedRoute user={user}>
                    <HelloTest />
                  </DevProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vote-analysis"
              element={
                <ProtectedRoute user={user}>
                  <DevProtectedRoute user={user}>
                    <VoteAnalysis />
                  </DevProtectedRoute>
                </ProtectedRoute>
              }
            />
          </Routes>
          </div>
        </div>
      </Router>
    </DialogProvider>
  );
};

// Dashboard component with onboarding check
const DashboardWithAdmin = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const onboardingComplete = await isOnboardingComplete(user.uid);
        if (!onboardingComplete) {
          setNeedsOnboarding(true);
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  const goToAdminPage = () => {
    navigate("/admin");
  };

  if (isLoading) {
    return (
      <div className="min-h-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (needsOnboarding) {
    return <OnboardingFlow user={user} />;
  }

  return (
    <>
      <Dashboard user={user} onLogout={onLogout} />
    </>
  );
};

const TermsPage = () => {
  return (
    <iframe 
      src="/InterpretosTerms.html" 
      style={{
        width: '100%',
        height: '100vh',
        border: 'none',
        overflow: 'auto'
      }}
      title="Terms and Conditions"
    />
  );
};

export default App;
